import React, { useContext } from 'react';

import { Checkbox, FormControlLabel, FormGroup, Tooltip } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { AxiosError } from 'axios';

import { FadeInComponent } from '@/components/FadeInComponent';
import { Team } from '@/core/api/collections/teams';
import { AuthContext } from '@/core/auth/AuthContext';
import { GRID_CLASSES, GRID_COLUMNS, ICON_COLUMN_SIZE } from '@/core/users/UserPermissions/UserPermissionsContentWidth';
import { ReactComponent as TeamsIcon } from '@/icons/teams.svg';

type Props = {
  teams?: Team[];
  loading: boolean;
  error?: AxiosError;
  selectedTeams: { [key: number]: Team | undefined };
  onChange: (team: Team, isSelected: boolean) => void;
}
export function EditTeams({ teams, loading, error, selectedTeams, onChange }: Props) {
  const { currentUser } = useContext(AuthContext);
  const canManageTeams = currentUser!.coreOwner || currentUser!.uvkAdmin;

  return (
    <FadeInComponent
      layout="position"
      className={`py-16 ${GRID_CLASSES}`}
      style={{ gridTemplateColumns: GRID_COLUMNS }}
    >
      <div className="col-start-2" style={{ height: ICON_COLUMN_SIZE }}>
        <div className="flex items-center justify-center h-full text-yellow-600 bg-yellow-200">
          <TeamsIcon className="w-5 h-5" />
        </div>
      </div>
      <div className="col-start-3 col-span-full">
        <h2 className="text-xl font-medium text-gray-900">Teams</h2>
        <p className="mt-1 font-light">Select which team(s) to add users to.</p>
      </div>
      <Tooltip title={canManageTeams ? '' : 'You do not have permission to manage teams. Please contact your account owner for assistance.'}>
        <FormGroup className="grid col-start-3 col-span-full" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
          {teams?.map((team: Team, index) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!selectedTeams[team.id]}
                  onChange={() => onChange(team, !selectedTeams[team.id])}
                  name={team.name}
                  color="primary"
                  disabled={!canManageTeams}
                />
              }
              label={team.name}
              key={`${team.name}${index}`}
            />
          ))}
          {(!teams && loading || error) && <LoadingRowSkeletons /> }
          {(!teams && !loading && !error) && <NoTeams />}
        </FormGroup>
      </Tooltip>
    </FadeInComponent>
  );
}

const LoadingRowSkeletons = () => {
  return (
    <>
      {[...Array(12)].map((_, index) => (
        <FormControlLabel
          control={<Checkbox />}
          label={<Skeleton variant="rect" className="w-20 h-5" />}
          key={`loading-chk-${index}`}
          checked={false}
        />
      ))}
    </>
  );
};

const NoTeams = () => {
  return (
    <p className="italic">
      No teams!
    </p>
  );
};
